/**
 * Build identity of the bundle currently running in this browser (issue #1354).
 *
 * `__APP_VERSION__` / `__BUILD_TIME__` are substituted by Vite at build time
 * (see vite.config.js). In a deployed build the version is the release tag that
 * also names the container image — `main-<sha>` on dev, `main-<sha>-prod` and
 * `main-<sha>-prod-us` on the two prod regions — so it identifies the deploy
 * exactly. Locally it is `local-<sha>[-dirty]`.
 *
 * The `typeof` guards matter: these globals only exist under Vite. Jest
 * transpiles the same modules with no `define` step, and this module must not
 * throw a ReferenceError there.
 */

declare const __APP_VERSION__: string;
declare const __BUILD_TIME__: string;

declare global {
  interface Window {
    /** Readable from the browser console on any deployment. */
    __COMPOSO_VERSION__?: { version: string; buildTime: string };
  }
}

export const APP_VERSION: string =
  typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "unknown";

export const BUILD_TIME: string =
  typeof __BUILD_TIME__ === "string" ? __BUILD_TIME__ : "";

/**
 * Human-readable build time, e.g. "12 Aug 2026, 14:03 UTC". Always UTC: the
 * point of comparison is a deploy log, not the reader's wall clock. Returns
 * null when the stamp is missing or unparseable, so callers render nothing
 * rather than "Invalid Date".
 */
export function formatBuildTime(isoTimestamp: string = BUILD_TIME): string | null {
  if (!isoTimestamp) return null;
  const parsed = new Date(isoTimestamp);
  if (Number.isNaN(parsed.getTime())) return null;
  return (
    parsed.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    }) + " UTC"
  );
}

/** One line to paste into a bug report or a deploy check. */
export function versionSummary(): string {
  const builtAt = formatBuildTime();
  return builtAt ? `${APP_VERSION} (built ${builtAt})` : APP_VERSION;
}

/**
 * Publish the build identity on `window` so anyone — including someone who
 * can't reproduce a bug — can read it out of the console without navigating
 * the UI. Called once at startup from main.tsx.
 */
export function publishVersionGlobal(): void {
  if (typeof window === "undefined") return;
  window.__COMPOSO_VERSION__ = { version: APP_VERSION, buildTime: BUILD_TIME };
}
